import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Table, Tabs, Pagination } from 'antd';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../Css/AnaliseComponente.css';
import api from '../../api/axiosConfig'; // Usar a instância axios configurada com token
import { 
  LineChartOutlined, 
  BarChartOutlined, 
  PieChartOutlined, 
  AreaChartOutlined, 
  BulbOutlined, 
  MenuOutlined 
} from '@ant-design/icons';

const AnaliseLinhas = () => {
  const navigate = useNavigate();

  // Aba 1: Dados de estatistica_atraso_rep_linhas
  const [repLinhasData, setRepLinhasData] = useState({});
  // Aba 3: Dados de estatistica_atraso_linhas
  const [atrasoLinhasData, setAtrasoLinhasData] = useState({});

  // Outros estados
  const [frequeData, setFrequeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState("1");

  // Estados para controle de página em cada aba paginada
  const [currentPageTab1, setCurrentPageTab1] = useState(1);
  const [currentPageTab3, setCurrentPageTab3] = useState(1);

  // Alerta de carregamento
  useEffect(() => {
    if (loading) {
      Swal.fire({
        title: 'Carregando...',
        text: 'Estamos processando as informações.',
        icon: 'info',
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });
    }
  }, [loading]);

  // Carregar os dados da API
  useEffect(() => {
    // Requisição para estatistica_atraso_rep_linhas (Aba 1)
    api.get('/estatistica_atraso_rep_linhas')
      .then(response => {
        setRepLinhasData(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Erro ao buscar os dados de Analise:", error);
        setLoading(false);
        Swal.fire({
          title: 'Erro!',
          text: 'Não foi possível carregar os dados de Analise.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      });

    // Requisição para freque_linhas (Aba 2)
    api.get('/freque_linhas')
      .then(response => {
        setFrequeData(response.data || []);
      })
      .catch(error => {
        console.error("Erro ao buscar os dados de frequência:", error);
        setFrequeData([]);
        Swal.fire({
          title: 'Erro!',
          text: 'Não foi possível carregar os dados de frequência.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      });

    // Requisição para estatistica_atraso_linhas (Aba 3)
    api.get('/estatistica_atraso_linhas')
      .then(response => {
        setAtrasoLinhasData(response.data);
      })
      .catch(error => {
        console.error("Erro ao buscar os dados de Dezenas sorteadas:", error);
        setAtrasoLinhasData({});
        Swal.fire({
          title: 'Erro!',
          text: 'Não foi possível carregar os dados de Dezenas sorteadas.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      });
  }, []);

  // Fechar o alerta de carregamento depois que os dados forem carregados
  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        Swal.close();
      }, 1500);
    }
  }, [loading]);

  const onTabChange = (key) => {
    setActiveKey(key);
  };

  // Colunas para Aba 1 (estatistica_atraso_rep_linhas)
  const repLinhasColumns = [
    { title: 'Dezenas', dataIndex: 'repetido', key: 'repetido' },
    { title: 'Quantidade', dataIndex: 'qte', key: 'qte' },
    { title: '%', dataIndex: 'percente', key: 'percente', render: (text) => `${text} %` },
    { title: 'Média Sorteio', dataIndex: 'mdSorteio', key: 'mdSorteio' },
    { title: 'Atrasados', dataIndex: 'atrasados', key: 'atrasados', render: (text, record) => (
        <span className={record.atrasados > record.mdSorteio ? 'highlight-high' : ''}>{text}</span>
      )},
    { title: 'Status Jogos', dataIndex: 'pj', key: 'pj', render: (text) => (
        <span className={text === 'Melhor Opção!' ? 'melhor-opcao' : ''}>{text}</span>
      )} 
  ];

  // Colunas para Aba 2 (freque_linhas)
  const frequeColumns = [
    { title: 'Dezenas', dataIndex: 'dezenas', key: 'dezenas' },
    { title: 'Quantidade', dataIndex: 'qte', key: 'qte' },
    { title: '%', dataIndex: 'percente', key: 'percente', render: (text) => `${text} %` },
    { title: 'Frequência', dataIndex: 'freqAcu', key: 'freqAcu',  render: (text) => `${text} - JG` },
    { title: 'Frequência AC', dataIndex: 'freqAcmlda', key: 'freqAcmlda', render: (text) => `${text} %` },
  ];

  // Colunas para Aba 3 (estatistica_atraso_linhas)
  const atrasoLinhasColumns = [
    { title: 'Numeros', dataIndex: 'dezenas', key: 'dezenas' },
    { title: 'Quantidade', dataIndex: 'qte', key: 'qte' },
    { title: '%', dataIndex: 'percente', key: 'percente', render: (text) => `${text} %` },
    { title: 'Média Sorteio', dataIndex: 'mdSorteio', key: 'mdSorteio' },
    { title: 'Atrasados', dataIndex: 'atrasados', key: 'atrasados', render: (text, record) => (
      <span className={record.atrasados > record.mdSorteio ? 'highlight-high' : ''}>{text}</span>
      )},
    { title: 'Status Jogos', dataIndex: 'pj', key: 'pj', render: (text) => (
        <span className={text === 'Melhor Opção!' ? 'melhor-opcao' : ''}>{text}</span>
      )} 
  ];

  // Dados da página atual para Aba 1
  const currentRepData = repLinhasData[`estatistica_atraso_rep_linha${currentPageTab1}`] || [];

  // Dados da página atual para Aba 3
  const currentAtrasoData = atrasoLinhasData[`estatistica_atraso_linha${currentPageTab3}`] || [];

  // Mapeamento para gráficos na Aba 1
  const repChartData = currentRepData.map(item => ({
    name: `${item.repetido} - Dz`,
    quantidade: item.qte,
    percentual: item.percente,
  }));

  // Mapeamento para gráficos na Aba 2
  const frequeChartData = frequeData.map(item => ({
    name: item.dezenas,
    quantidade: item.qte,
    percentual: item.percente,
    frequencia: item.freqAcu,
    frequenciaAc: item.freqAcmlda,
  }));

  // Mapeamento para gráficos na Aba 3
  const atrasoChartData = currentAtrasoData.map(item => ({
    name: `Nu - ${item.dezenas}`,
    quantidade: item.qte,
    percentual: item.percente,
  }));

  const tabsItems = [
    {
      label: <LineChartOutlined className="icon-size-24" />,
      key: "1",
      children: (
        <div>
          <h2><span>Análise - Linhas</span></h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart width={500} height={400} data={repChartData}>
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis dataKey="name" scale="band" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantidade" barSize={50} fill="#007AA2" />
              <Line type="monotone" dataKey="percentual" stroke="#ff7300" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="table-container">
            <Table 
              columns={repLinhasColumns} 
              dataSource={currentRepData} 
              loading={loading} 
              rowKey="id" 
              pagination={false} 
            />
          </div>
          <Pagination
            current={currentPageTab1}
            total={Object.keys(repLinhasData).length}
            pageSize={1}
            onChange={(page) => setCurrentPageTab1(page)}
            style={{ marginTop: 16, textAlign: 'center' }}
            itemRender={(page, type, originalElement) => {
              if (type === 'page') {
                return <a>{`Linha ${page}`}</a>;
              }
              return originalElement;
            }}
          />
        </div>
      )
    },
    {
      label: <BarChartOutlined className="icon-size-24" />,
      key: "2",
      children: (
        <div>
          <h2><span>Frequência - Linhas</span></h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart width={500} height={400} data={frequeChartData}>
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis dataKey="name" scale="band" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantidade" stackId="a" barSize={50} fill="#004B57" />
              <Bar dataKey="percentual" stackId="a" barSize={50} fill="#ff7300" />
              <Bar dataKey="frequencia" stackId="a" barSize={50} fill="#007AA2" />
              <Bar dataKey="frequenciaAc" stackId="a" barSize={50} fill="#008DC0" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="table-container">
          <Table
              columns={frequeColumns} 
              dataSource={frequeData} 
              loading={loading} 
              rowKey="id" 
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ['10','20','50','100'],
                locale: {
                  items_per_page: ' / página',  // Texto exibido após o número de itens
                  jump_to: 'Ir para',
                  page: 'Página',
                  prev_page: 'Página Anterior',
                  next_page: 'Próxima Página',
                  prev_5: 'Voltar 5',
                  next_5: 'Avançar 5',
                  prev_3: 'Voltar 3',
                  next_3: 'Avançar 3',
                }
              }}
            />
          </div>
        </div>
      )
    },
    {
      label: <AreaChartOutlined className="icon-size-24" />,
      key: "3",
      children: (
        <div>
          <h2><span>Dezenas - Linhas</span></h2>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart width={500} height={400} data={atrasoChartData}>
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis dataKey="name" scale="band" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantidade" barSize={50} fill="#007AA2" />
              <Line type="monotone" dataKey="percentual" stroke="#ff7300" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="table-container">
            <Table 
              columns={atrasoLinhasColumns} 
              dataSource={currentAtrasoData} 
              loading={loading} 
              rowKey="id" 
              pagination={false} 
            />
          </div>
          <Pagination
            current={currentPageTab3}
            total={Object.keys(atrasoLinhasData).length}
            pageSize={1}
            onChange={(page) => setCurrentPageTab3(page)}
            style={{ marginTop: 16, textAlign: 'center' }}
            itemRender={(page, type, originalElement) => {
              if (type === 'page') {
                return <a>{`Linha ${page}`}</a>;
              }
              return originalElement;
            }}
          />
        </div>
      )
    },
    {
      label: <BulbOutlined className="icon-size-24" />,
      key: "4",
      children: (
        <div>
          <h2><span>Ideias Dz - Linhas</span></h2>
        </div>
      )
    },
    {
      label: <MenuOutlined className="icon-size-24" onClick={() => navigate("/dashboard")} />,
      key: "5",      
    }
  ];

  return (
    <div className="analysis-container">
      <Tabs
        activeKey={activeKey}
        onChange={onTabChange}
        tabPosition={"right"}
        items={tabsItems}
      />
    </div>
  );
};

export default AnaliseLinhas;
